import { EQUIPMENT_SLOTS, ITEM_CATEGORIES, ITEM_CONDITION_MODELS, ITEM_ICON_KEYS, ITEM_SCARCITIES, ITEM_TAGS, ITEM_WORLD_ASSET_IDS, } from "./types.js";
export const ITEM_STACK_LIMIT_MAXIMUM = 10_000;
const DEFINITION_ID_PATTERN = /^[a-z][a-z0-9_]*(?:\.[a-z0-9_]+)+$/;
export function validateItemDefinitions(definitions) {
    const errors = [];
    const index = new Map();
    for (const definition of definitions) {
        const prefix = definition?.id || "<missing-id>";
        if (!DEFINITION_ID_PATTERN.test(definition?.id ?? "") || definition.id.length > 96) {
            errors.push(`${prefix}: invalid stable definition ID`);
        }
        if (index.has(definition.id))
            errors.push(`${prefix}: duplicate definition ID`);
        if (!definition.name.trim() || definition.name.length > 80)
            errors.push(`${prefix}: invalid name`);
        if (definition.shortName !== undefined
            && (!definition.shortName.trim() || definition.shortName.length > 40)) {
            errors.push(`${prefix}: invalid shortName`);
        }
        if (!definition.description.trim() || definition.description.length > 240)
            errors.push(`${prefix}: invalid description`);
        if (!ITEM_CATEGORIES.includes(definition.category))
            errors.push(`${prefix}: invalid category`);
        if (!Number.isSafeInteger(definition.stackLimit) || definition.stackLimit < 1 || definition.stackLimit > ITEM_STACK_LIMIT_MAXIMUM) {
            errors.push(`${prefix}: invalid stackLimit`);
        }
        if (!Number.isSafeInteger(definition.unitMassGrams) || definition.unitMassGrams < 0 || definition.unitMassGrams > 10_000_000) {
            errors.push(`${prefix}: invalid unitMassGrams`);
        }
        if (!Number.isFinite(definition.volumeMl) || definition.volumeMl <= 0 || definition.volumeMl > 1_000_000)
            errors.push(`${prefix}: invalid volumeMl`);
        if (!Number.isFinite(definition.longestDimensionMm) || definition.longestDimensionMm <= 0 || definition.longestDimensionMm > 10_000)
            errors.push(`${prefix}: invalid longestDimensionMm`);
        if (definition.battery) {
            if (!definition.battery.format || !Number.isFinite(definition.battery.capacityWh) || definition.battery.capacityWh <= 0 || definition.battery.capacityWh > 1000 || definition.stackLimit !== 1)
                errors.push(`${prefix}: invalid battery/nonstackable state`);
        }
        if (definition.lightDevice) {
            const light = definition.lightDevice;
            if (definition.stackLimit !== 1 || !['LED', 'INCANDESCENT'].includes(light.emitterType)
                || ![light.luminousFluxLumens, light.colorTemperatureKelvin, light.beamAngleDegrees, light.effectiveRangeMeters, light.powerDrawWatts].every(n => Number.isFinite(n) && n > 0)
                || light.luminousFluxLumens > 100_000 || light.colorTemperatureKelvin > 20_000 || light.beamAngleDegrees > 120 || light.effectiveRangeMeters > 500 || light.powerDrawWatts > 1000
                || !Number.isFinite(light.beamSoftness) || light.beamSoftness < 0 || light.beamSoftness > 1 || !light.battery.format || !Number.isInteger(light.battery.count) || light.battery.count < 1 || light.battery.count > 8)
                errors.push(`${prefix}: invalid light device`);
        }
        if (definition.wearable) {
            const garment = definition.wearable;
            if (definition.category !== 'CLOTHING' || definition.stackLimit !== 1 || !EQUIPMENT_SLOTS.includes(garment.equipmentSlot)
                || !garment.visualId || !Number.isFinite(garment.insulationClo) || garment.insulationClo < 0 || garment.insulationClo > 10
                || garment.pockets.length > 16 || new Set(garment.pockets.map(p => p.id)).size !== garment.pockets.length)
                errors.push(`${prefix}: invalid wearable`);
            const closures = new Set(garment.closures.map(c => c.id));
            if (closures.size !== garment.closures.length || garment.closures.some(c => !c.id || !Number.isFinite(c.openSeconds) || c.openSeconds < 0 || c.openSeconds > 15 || !Number.isFinite(c.openInsulationMultiplier) || c.openInsulationMultiplier < 0 || c.openInsulationMultiplier > 1))
                errors.push(`${prefix}: invalid closures`);
            for (const pocket of garment.pockets) {
                if (!/^[A-Z0-9_]{1,40}$/.test(pocket.id) || !pocket.name || !Number.isFinite(pocket.capacityMl) || pocket.capacityMl <= 0 || pocket.capacityMl > 100_000
                    || !Number.isFinite(pocket.maxDimensionMm) || pocket.maxDimensionMm <= 0 || pocket.maxDimensionMm > 2000
                    || ![pocket.baseAccessSeconds, pocket.fillPenaltySeconds, pocket.burialPenaltySeconds, pocket.obstructionPenaltySeconds ?? 0].every(n => Number.isFinite(n) && n >= 0 && n <= 30)
                    || pocket.baseAccessSeconds < 0.1 || typeof pocket.quickAccessEligible !== 'boolean'
                    || (pocket.requiresOpenClosure && !closures.has(pocket.requiresOpenClosure))
                    || (pocket.obstructedByPocketId && !garment.pockets.some(p => p.id === pocket.obstructedByPocketId))
                    || pocket.allowedCategories?.some(category => !ITEM_CATEGORIES.includes(category)))
                    errors.push(`${prefix}: invalid pocket ${pocket.id}`);
            }
        }
        if (!ITEM_ICON_KEYS.includes(definition.iconKey))
            errors.push(`${prefix}: invalid iconKey`);
        if (definition.worldAssetId !== undefined && !ITEM_WORLD_ASSET_IDS.includes(definition.worldAssetId)) {
            errors.push(`${prefix}: invalid worldAssetId`);
        }
        if (definition.scarcity !== undefined && !ITEM_SCARCITIES.includes(definition.scarcity)) {
            errors.push(`${prefix}: invalid scarcity`);
        }
        if (!Array.isArray(definition.tags) || new Set(definition.tags).size !== definition.tags.length) {
            errors.push(`${prefix}: duplicate or invalid tags`);
        }
        else if (definition.tags.some((tag) => !ITEM_TAGS.includes(tag))) {
            errors.push(`${prefix}: unknown item tag`);
        }
        if (definition.conditionModel !== undefined
            && !ITEM_CONDITION_MODELS.includes(definition.conditionModel)) {
            errors.push(`${prefix}: invalid conditionModel`);
        }
        if (definition.conditionModel === "DURABILITY") {
            if (!Number.isSafeInteger(definition.durabilityMax) || (definition.durabilityMax ?? 0) < 1) {
                errors.push(`${prefix}: durabilityMax required for durability model`);
            }
            if (definition.stackLimit !== 1)
                errors.push(`${prefix}: durability items must be non-stackable`);
        }
        else if (definition.durabilityMax !== undefined) {
            errors.push(`${prefix}: durabilityMax requires durability model`);
        }
        if (definition.sortOrder !== undefined && !Number.isSafeInteger(definition.sortOrder)) {
            errors.push(`${prefix}: invalid sortOrder`);
        }
        index.set(definition.id, deepFreezeDefinition(definition));
    }
    if (errors.length > 0)
        throw new Error(`Invalid item catalog:\n${errors.join("\n")}`);
    return new ImmutableMap(index);
}
function deepFreezeDefinition(definition) {
    return deepFreeze(JSON.parse(JSON.stringify(definition)));
}
/** Read-only facade over the owned O(1) index; no mutable Map reference escapes. */
class ImmutableMap {
    #index;
    constructor(index) {
        this.#index = index;
        Object.freeze(this);
    }
    get size() { return this.#index.size; }
    get(key) { return this.#index.get(key); }
    has(key) { return this.#index.has(key); }
    entries() { return this.#index.entries(); }
    keys() { return this.#index.keys(); }
    values() { return this.#index.values(); }
    [Symbol.iterator]() { return this.#index[Symbol.iterator](); }
    forEach(callbackfn, thisArg) {
        for (const [key, value] of this.#index)
            callbackfn.call(thisArg, value, key, this);
    }
}
export function deepFreeze(value) {
    if (value && typeof value === 'object' && !Object.isFrozen(value)) {
        for (const child of Object.values(value))
            deepFreeze(child);
        Object.freeze(value);
    }
    return value;
}
